import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon, MobileHeader, EmptyState, useToast } from '@/components';
import { useCourse, useCheckIn } from '@/hooks/queries';
import { useLocale } from '@/i18n/LocaleProvider';
import { formatDate } from '@/lib/format';
import { QrScanner } from './QrScanner';

// Server error code → learner-facing message.
const ERRORS: Record<string, string> = {
  checkin_closed: 'Check-in isn’t open yet — ask the instructor to open it.',
  invalid_token: 'That QR code isn’t valid for this session.',
  not_enrolled: 'You’re not enrolled in this course.',
  lessons_incomplete: 'Finish all the lessons before you check in.',
};

/**
 * Attendance check-in. Two ways in, both calling check_in():
 *  1. In-app scanner — tap "Scan venue QR" → camera opens in the app, decodes the
 *     venue QR, marks present. No native-camera hop, so the learner stays logged in.
 *  2. Auto — arriving via a native-camera scan of …/?checkin=<token> marks present.
 * The instructor/helper tap-to-mark is the fallback when a camera won't cooperate.
 */
export function LearnerAttendance() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('checkin');
  const navigate = useNavigate();
  const { data: course } = useCourse(id);
  const checkIn = useCheckIn();
  const { toast } = useToast();
  const { locale } = useLocale();
  const [checked, setChecked] = useState(false);
  const [scanning, setScanning] = useState(false);
  const tried = useRef(false);
  // Check-in is gated behind finishing every lesson.
  const lessonsComplete = !!course && (course.lessons.length === 0 || course.lessons.every((l) => l.done));

  const runCheckIn = useCallback(
    (scanToken: string) => {
      checkIn.mutate(
        { courseId: id, token: scanToken },
        {
          onSuccess: () => setChecked(true),
          onError: (err) => {
            // Supabase PostgrestError isn't an `instanceof Error`, so read `.message` directly.
            const msg = (err as { message?: string } | null)?.message ?? String(err);
            const key = Object.keys(ERRORS).find((k) => msg.includes(k));
            toast(key ? ERRORS[key] : 'Could not check you in.', { tone: 'clay', icon: 'x' });
          },
        },
      );
    },
    [checkIn, id, toast],
  );

  // In-app scan: the venue QR encodes …/?checkin=<token>&course=<id> — pull the token out.
  const handleScan = useCallback(
    (text: string) => {
      setScanning(false);
      let scanToken = text.trim();
      try {
        const u = new URL(text);
        scanToken = u.searchParams.get('checkin') || scanToken;
      } catch {
        /* not a URL — use the raw text as the token */
      }
      runCheckIn(scanToken);
    },
    [runCheckIn],
  );

  // Arriving via a native-camera scan (URL carries ?checkin) marks present automatically.
  useEffect(() => {
    if (!token || !course || tried.current || !lessonsComplete) return;
    tried.current = true;
    runCheckIn(token);
  }, [token, course, runCheckIn, lessonsComplete]);

  if (!course) return <EmptyState icon="qr" title="Session not found" />;
  const formattedSeminarDate = formatDate(course.seminarDate, locale, { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader title="Attendance" th="การเข้าร่วม" onBack={() => navigate(`/learn/learning/${id}`)} large={false} />
      <div className="hidden md:block" style={{ padding: '18px 28px 0' }}>
        <button onClick={() => navigate(`/learn/learning/${id}`)} className="btn btn-ghost btn-sm"><Icon name="chevL" size={15} /> Back to course</button>
      </div>
      <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20, padding: '18px 20px 28px' }}>
        <div className="card" style={{ padding: 16 }}>
          <div className="th" style={{ fontSize: 16, fontWeight: 600 }}>{course.title.th}</div>
          <div style={{ display: 'flex', gap: 10, fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>
            <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}><Icon name="cal" size={14} /> {course.seminar}</span>
            <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}><Icon name="pin" size={14} /> {course.venue}</span>
          </div>
        </div>

        {checked ? (
          <div className="card" style={{ padding: '36px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, animation: 'fadeUp .4s both' }}>
            <div style={{ width: 88, height: 88, borderRadius: '50%', background: 'var(--sage-soft)', display: 'grid', placeItems: 'center', color: 'var(--sage)', animation: 'pop .5s cubic-bezier(.2,.7,.2,1) both' }}>
              <Icon name="check" size={44} stroke={2.2} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: '18px 0 2px' }}>Checked in</h2>
            <div className="th" style={{ color: 'var(--ink-3)', fontSize: 14 }}>เช็คอินสำเร็จ</div>
            <div className="u-mono" style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 10 }}>{course.venue}</div>
            <button className="btn btn-primary btn-lg" style={{ marginTop: 18, minWidth: 240 }} onClick={() => navigate(`/learn/learning/${id}`)}>
              Continue · ต่อไป <Icon name="arrowR" size={17} />
            </button>
          </div>
        ) : !lessonsComplete ? (
          <div className="card" style={{ padding: '32px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--ochre-soft)', display: 'grid', placeItems: 'center', color: 'var(--ochre)' }}>
              <Icon name="lock" size={30} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Finish the lessons first</div>
            <p className="th" style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0, maxWidth: 320 }}>
              เรียนบทเรียนให้ครบทุกบทก่อน จึงจะเช็คอินได้ · Complete every lesson before you can check in.
            </p>
            <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate(`/learn/learning/${id}`)}>
              <Icon name="book" size={17} /> Go to lessons
            </button>
          </div>
        ) : checkIn.isPending ? (
          <div className="card" style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div className="eyebrow">Checking you in…</div>
          </div>
        ) : scanning ? (
          <QrScanner onScan={handleScan} onCancel={() => setScanning(false)} />
        ) : (
          <div className="card" style={{ padding: '32px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--card-2)', display: 'grid', placeItems: 'center', color: 'var(--sage)' }}>
              <Icon name="qr" size={32} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Scan the venue QR to check in</div>
            <p className="th" style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0, maxWidth: 320 }}>
              กดปุ่มด้านล่างเพื่อเปิดกล้องในแอป แล้วสแกนคิวอาร์ที่จุดลงทะเบียน · Tap below to open the in-app camera and scan the QR at the registration desk.
            </p>
            <button className="btn btn-primary btn-lg btn-block" onClick={() => setScanning(true)}>
              <Icon name="qr" size={18} /> Scan venue QR · สแกนเช็คอิน
            </button>
            <div className="th" style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              <Icon name="cal" size={15} style={{ flex: 'none' }} /> {course.seminar} · {formattedSeminarDate}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--ink-3)', padding: '0 4px' }}>
          <Icon name="user" size={15} style={{ flex: 'none', marginTop: 1 }} /> Can’t scan? A staff member at the desk can mark you present.
        </div>
      </div>
    </div>
  );
}
